import { toSavePayload } from '@apis/schedule';
import type { ISchedulerData } from '@interfaces/IScheduler';
import { planFields } from './planFields';
import { sameJson } from './sameJson';
import { splitScheduleData } from './taskViewCodec';

/** What a Save would PUT, minus the version token (that's concurrency, not
 *  content), read through `planFields` — view state (fold) dropped, every
 *  spelling of "unset" equal — exactly as the Save review reads it. */
function planContent(data: ISchedulerData) {
  const { baseVersion: _version, groups, tasks, ...content } = toSavePayload(splitScheduleData(data).plan);
  return {
    ...content,
    groups: groups.map(planFields),
    // An area's STATUS is Trusstor's live field state (the store follows the
    // feed; the saved copy keeps the doc's), not a plan edit — only the
    // assigned set is. Same rule as the Save review's areas row.
    tasks: tasks.map((task) => planFields({ ...task, locations: (task.locations ?? []).map((l) => l.locationId).sort() })),
  };
}

/**
 * True when Save would actually change something: the working copy's save
 * payload differs from the last saved copy's. This — not "an editing action
 * happened" — is what "unsaved changes" means. Undoing back to the saved
 * state, a drawer Save with nothing edited, or editing a value and typing it
 * back all leave the two equal, so Save stays off.
 */
export function hasUnsavedPlanChanges(saved: ISchedulerData, working: ISchedulerData): boolean {
  return !sameJson(planContent(saved), planContent(working));
}

/**
 * The Save button's "unsaved changes" state. The store's dirty flag says an
 * editing action happened; it never un-sets on its own (undo, a no-op drawer
 * Save and a retried-out immediate write all leave it on), which lit Save
 * with nothing to save. So the flag is necessary but not sufficient: there
 * must also be a real difference from the saved copy. With nothing to
 * compare against, trust the flag rather than hide an edit.
 */
export function isScheduleDirty(
  flagged: boolean,
  saved: ISchedulerData | undefined,
  working: ISchedulerData | undefined,
): boolean {
  if (!flagged) return false;
  if (!saved || !working) return true;
  return hasUnsavedPlanChanges(saved, working);
}
